import React, { useState } from "react";
import { type RouterOutputs, trpc } from "@/hooks/trpc";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { MEDIUM_SOFT_BG_COLORS } from "@/lib/color-utils";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { PlanFermentationDialog } from "@/features/fermentation-journal/components/planning/plan-fermentation-dialog";
import { InvestigationsStrip } from "@/features/fermentation-investigation/investigations-strip";

type FermentationListItem = RouterOutputs["fermentations"]["list"][number] & {
  group_name: string | null;
  group_label: string | null;
  equipment_model_name: string | null;
  status: string | null;
};

// "{name} · {label}", the way the group is shown everywhere else.
function displayedGroupName(row: FermentationListItem): string | null {
  return row.group_label
    ? `${row.group_name} · ${row.group_label}`
    : row.group_name;
}

export const Route = createFileRoute("/fermentations/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [rawData] = trpc.fermentations.list.useSuspenseQuery();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Cast to our extended type since the backend does return these fields
  const fermentationsData = rawData as FermentationListItem[];

  // Tank count per group — colors are hidden for single-tank groups.
  const tankCountByGroup = React.useMemo(() => {
    const map = new Map<number | string, number>();
    for (const f of fermentationsData) {
      const key = f.fermentation_group_id;
      if (key == null) continue;
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [fermentationsData]);

  const columns = React.useMemo(() => {
    const baseColumns: DataTableColumnDef<FermentationListItem, unknown>[] = [
      {
        id: "group_name",
        // The label is part of the value so search finds it too.
        accessorFn: displayedGroupName,
        // Group by identity, not by the displayed text.
        getGroupingValue: (row) => row.fermentation_group_id,
        header: "Group",
        enableGrouping: true,
        cell: ({ row }) => (
          <Link
            to="/fermentations/$group_id"
            params={{
              group_id: row.original.fermentation_group_id!,
            }}
            className="hover:underline font-medium"
          >
            {displayedGroupName(row.original)}
          </Link>
        ),
        size: 180,
        meta: {
          leafCell: ({ row }) => {
            const groupId = row.original.fermentation_group_id;
            const showColor =
              groupId != null && (tankCountByGroup.get(groupId) ?? 0) > 1;
            return (
              <div className="pl-8 flex items-center gap-1.5">
                {showColor && (
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: row.original.color_code
                        ? MEDIUM_SOFT_BG_COLORS[row.original.color_code]
                        : "transparent",
                    }}
                  />
                )}
                <span>{row.original.name}</span>
              </div>
            );
          },
        },
        enableColumnFilter: true,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => {
          if (row.status) return row.status;
          if (row.started_at && !row.ended_at) return "Running";
          if (row.ended_at) return "Ended";
          return "Planned";
        },
        cell: ({ row }) => {
          if (row.original.status) {
            const statusColors: Record<
              string,
              "default" | "secondary" | "outline" | "destructive"
            > = {
              PLANNED: "secondary",
              PREPARING: "secondary",
              PREPARED: "secondary",
              RUNNING: "default",
              ENDED: "outline",
              ARCHIVED: "outline",
            };
            return (
              <Badge variant={statusColors[row.original.status] ?? "secondary"}>
                {row.original.status}
              </Badge>
            );
          }

          const { started_at, ended_at } = row.original;
          let status = "Planned";
          let variant: "default" | "secondary" | "outline" = "secondary";

          if (started_at && !ended_at) {
            status = "Running";
            variant = "default";
          } else if (ended_at) {
            status = "Ended";
            variant = "outline";
          }

          return <Badge variant={variant}>{status}</Badge>;
        },
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "equipment_model_name",
        accessorKey: "equipment_model_name",
        header: "Fermentor",
        cell: ({ row }) => {
          const model = row.original.equipment_model_name;
          return model ? (
            <Badge variant="outline">{model}</Badge>
          ) : null;
        },
        size: 120,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "cultivation_type",
        accessorKey: "cultivation_type",
        header: "Type",
        cell: ({ row }) => {
          const type = row.original.cultivation_type;
          return type ? <Badge variant="outline">{type}</Badge> : null;
        },
        size: 100,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "started_at",
        accessorKey: "started_at",
        header: "Started",
        cellTypeId: "date",
        aggregationFn: "max",
        size: 110,
        enableSorting: true,
      },
      {
        id: "ended_at",
        accessorKey: "ended_at",
        header: "Ended",
        cellTypeId: "date",
        aggregationFn: "max",
        size: 110,
        enableSorting: true,
      },
    ];

    return baseColumns;
  }, [tankCountByGroup]);

  return (
    <div className="container mx-auto flex flex-col min-h-0 pt-4 grow gap-4">
      {/* Dialog */}
      <PlanFermentationDialog open={dialogOpen} onOpenChange={setDialogOpen} />

      <InvestigationsStrip />

      {/* Fermentations Table */}
      <DataTable
        data={fermentationsData}
        columns={columns}
        defaultExpanded
        defaultGrouping={["group_name"]}
        defaultSorting={[{ id: "started_at", desc: true }]}
        searchPlaceholder="Search fermentations..."
        enableGrouping={true}
        hardcodedGrouping={true}
        enableColumnResizing={true}
        leftCustomToolbar={
          <Button onClick={() => setDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Fermentation
          </Button>
        }
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />
    </div>
  );
}
